import React, { Component } from "react";
import {
  Table,
  Row,
  Col,
  FormControl,
  Button,
  Spinner,
  Modal,
  Form,
} from "react-bootstrap";
import CartContext from "../StoreManager/CartContext";
import { fetchCrmList, saveCrmItem, deleteCrmItem } from "./CrmApi";

const emptyInvoice = {
  invoiceId: "",
  storeId: "",
  invoiceNumber: "",
  customerName: "",
  contactId: "",
  status: "",
  invoiceDate: "",
  dueDate: "",
  totalAmount: "",
  description: "",
};

export default class CrmInvoices extends Component {
  static contextType = CartContext;

  constructor(props) {
    super(props);
    this.state = {
      invoices: [],
      filteredInvoices: [],
      loading: true,
      search: "",
      error: null,
      showModal: false,
      isEdit: false,
      currentInvoice: { ...emptyInvoice },
    };
  }

  componentDidMount() {
    this.loadInvoices();
  }

  loadInvoices = async () => {
    const storeId = this.context.storeId;
    if (!storeId) {
      this.setState({ loading: false, error: "StoreId is required." });
      return;
    }

    this.setState({ loading: true, error: null });
    try {
      const invoices = await fetchCrmList("Invoices", "GetInvoices", storeId);
      const normalized = Array.isArray(invoices) ? invoices : [];
      this.setState({
        invoices: normalized,
        filteredInvoices: normalized,
        loading: false,
      });
    } catch (error) {
      this.setState({
        loading: false,
        error: error.message || "Unable to load invoices.",
      });
    }
  };

  onSearchChange = (event) => {
    const search = event.target.value || "";
    const normalizedSearch = search.trim().toLowerCase();
    const filteredInvoices = this.state.invoices.filter((invoice) => {
      const number = (invoice.invoiceNumber || "").toLowerCase();
      const customer = (invoice.customerName || "").toLowerCase();
      const status = (invoice.status || "").toLowerCase();
      return (
        number.includes(normalizedSearch) ||
        customer.includes(normalizedSearch) ||
        status.includes(normalizedSearch)
      );
    });

    this.setState({ search, filteredInvoices });
  };

  formatDate = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleDateString();
  };

  handleShowModal = (invoice = null) => {
    this.setState({
      currentInvoice: invoice ? { ...invoice } : { ...emptyInvoice },
      isEdit: Boolean(invoice),
      showModal: true,
      error: null,
    });
  };

  handleCloseModal = () => {
    this.setState({ showModal: false, error: null });
  };

  handleFieldChange = (field, value) => {
    this.setState((prevState) => ({
      currentInvoice: {
        ...prevState.currentInvoice,
        [field]: value,
      },
    }));
  };

  saveInvoice = async () => {
    const storeId = this.context.storeId;
    if (!storeId) {
      this.setState({ error: "StoreId is required." });
      return;
    }

    const invoice = {
      ...this.state.currentInvoice,
      storeId,
      invoiceId:
        this.state.currentInvoice.invoiceId ||
        "00000000-0000-0000-0000-000000000000",
      totalAmount: this.state.currentInvoice.totalAmount
        ? Number(this.state.currentInvoice.totalAmount)
        : 0,
      invoiceDate: this.state.currentInvoice.invoiceDate || null,
      dueDate: this.state.currentInvoice.dueDate || null,
    };

    try {
      await saveCrmItem("Invoices", "AddOrUpdateInvoice", invoice);
      await this.loadInvoices();
      this.handleCloseModal();
    } catch (error) {
      this.setState({ error: error.message || "Unable to save invoice." });
    }
  };

  deleteInvoice = async (invoiceId) => {
    const storeId = this.context.storeId;
    if (!storeId) return;
    if (!window.confirm("Delete this invoice?")) return;

    try {
      await deleteCrmItem("Invoices", "DeleteInvoice", storeId, invoiceId);
      await this.loadInvoices();
    } catch (error) {
      this.setState({ error: error.message || "Unable to delete invoice." });
    }
  };

  render() {
    const {
      filteredInvoices,
      loading,
      search,
      error,
      showModal,
      isEdit,
      currentInvoice,
    } = this.state;
    const storeId = this.context.storeId;

    return (
      <div className="m-3">
        <Row className="align-items-center mb-3">
          <Col>
            <h2>CRM Invoices</h2>
            <p className="text-muted">
              Manage CRM invoices for the selected store.
            </p>
          </Col>
          <Col xs="auto" className="d-flex gap-2">
            <Button variant="primary" onClick={() => this.handleShowModal()}>
              Add Invoice
            </Button>
            <Button
              variant="secondary"
              onClick={this.loadInvoices}
              disabled={loading}
            >
              {loading ? <Spinner animation="border" size="sm" /> : "Refresh"}
            </Button>
          </Col>
        </Row>

        {!storeId && (
          <div className="alert alert-warning">
            StoreId is required to load CRM invoices.
          </div>
        )}
        {error && <div className="alert alert-danger">{error}</div>}

        <Row className="mb-3">
          <Col md={6} lg={4}>
            <FormControl
              placeholder="Search by invoice number, customer, or status"
              value={search}
              onChange={this.onSearchChange}
            />
          </Col>
        </Row>

        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Invoice No.</th>
              <th>Customer</th>
              <th>Status</th>
              <th>Date</th>
              <th>Due</th>
              <th>Amount</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="7" className="text-center py-5">
                  <Spinner animation="border" /> Loading...
                </td>
              </tr>
            ) : filteredInvoices.length === 0 ? (
              <tr>
                <td colSpan="7" className="text-center py-5">
                  No invoices found.
                </td>
              </tr>
            ) : (
              filteredInvoices.map((invoice) => (
                <tr
                  key={
                    invoice.invoiceId || invoice.invoiceNumber || Math.random()
                  }
                >
                  <td>{invoice.invoiceNumber || "-"}</td>
                  <td>{invoice.customerName || "-"}</td>
                  <td>{invoice.status || "-"}</td>
                  <td>{this.formatDate(invoice.invoiceDate)}</td>
                  <td>{this.formatDate(invoice.dueDate)}</td>
                  <td>
                    {invoice.totalAmount != null ? invoice.totalAmount : "-"}
                  </td>
                  <td>
                    <Button
                      size="sm"
                      variant="outline-primary"
                      className="me-2"
                      onClick={() => this.handleShowModal(invoice)}
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={() => this.deleteInvoice(invoice.invoiceId)}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>

        <Modal show={showModal} onHide={this.handleCloseModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>{isEdit ? "Edit Invoice" : "Add Invoice"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Row className="g-3">
                <Col md={6}>
                  <Form.Group controlId="invoiceNumber">
                    <Form.Label>Invoice Number</Form.Label>
                    <Form.Control
                      value={currentInvoice.invoiceNumber}
                      onChange={(e) =>
                        this.handleFieldChange("invoiceNumber", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="customerName">
                    <Form.Label>Customer Name</Form.Label>
                    <Form.Control
                      value={currentInvoice.customerName}
                      onChange={(e) =>
                        this.handleFieldChange("customerName", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="invoiceDate">
                    <Form.Label>Invoice Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={currentInvoice.invoiceDate?.split("T")[0] || ""}
                      onChange={(e) =>
                        this.handleFieldChange("invoiceDate", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="dueDate">
                    <Form.Label>Due Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={currentInvoice.dueDate?.split("T")[0] || ""}
                      onChange={(e) =>
                        this.handleFieldChange("dueDate", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="totalAmount">
                    <Form.Label>Total Amount</Form.Label>
                    <Form.Control
                      type="number"
                      value={currentInvoice.totalAmount}
                      onChange={(e) =>
                        this.handleFieldChange("totalAmount", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="status">
                    <Form.Label>Status</Form.Label>
                    <Form.Control
                      value={currentInvoice.status}
                      onChange={(e) =>
                        this.handleFieldChange("status", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="contactId">
                    <Form.Label>Contact Id</Form.Label>
                    <Form.Control
                      value={currentInvoice.contactId}
                      onChange={(e) =>
                        this.handleFieldChange("contactId", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group controlId="description">
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={currentInvoice.description}
                      onChange={(e) =>
                        this.handleFieldChange("description", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={this.handleCloseModal}>
              Cancel
            </Button>
            <Button variant="primary" onClick={this.saveInvoice}>
              Save
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  }
}
